/**
 * Capture taxonomy — the single source of truth for how an artifact came into
 * being. Two orthogonal axes:
 *
 *   captureMethod    — what kind of content this is and which pipeline produced
 *                      it (image_to_text, audio_to_text, manual_text, ...).
 *                      Used for analytics filters and per-feature cost queries.
 *
 *   captureSurface   — where the user was when the artifact was created
 *                      (desktop / mobile / live_session / agent_chat / connector
 *                      / api). Decoupled from method so we can answer
 *                      "top mobile users" independent of feature usage.
 *
 * Provenance (the specific source artifact that derived content was extracted
 * from) lives in the relationships table — see gri/relationship-types.ts for
 * the extracted_from / transcribed_from / imported_from / summarised_from /
 * copied_from edges.
 */
// --- captureMethod ----------------------------------------------------------
/** Native typed text — user typed into a note. */
export const CAPTURE_METHOD_MANUAL_TEXT = 'manual_text';
/** Raw uploads (artifactType='attachment'). */
export const CAPTURE_METHOD_IMAGE_UPLOAD = 'image_upload';
export const CAPTURE_METHOD_AUDIO_UPLOAD = 'audio_upload';
export const CAPTURE_METHOD_VIDEO_UPLOAD = 'video_upload';
export const CAPTURE_METHOD_PDF_UPLOAD = 'pdf_upload';
export const CAPTURE_METHOD_DOCX_UPLOAD = 'docx_upload';
export const CAPTURE_METHOD_CSV_UPLOAD = 'csv_upload';
/** An .xlsx workbook imported into a native spreadsheet. */
export const CAPTURE_METHOD_XLSX_UPLOAD = 'xlsx_upload';
/** Image dropped or pasted directly into the TipTap editor. */
export const CAPTURE_METHOD_EDITOR_PASTE = 'editor_paste';
/** Derived notes (artifactType='note', source recorded as a provenance edge). */
export const CAPTURE_METHOD_IMAGE_TO_TEXT = 'image_to_text';
export const CAPTURE_METHOD_AUDIO_TO_TEXT = 'audio_to_text';
export const CAPTURE_METHOD_PDF_TO_TEXT = 'pdf_to_text';
export const CAPTURE_METHOD_DOCX_TO_TEXT = 'docx_to_text';
/** One note per CSV row produced by the batch importer. */
export const CAPTURE_METHOD_CSV_TO_NOTES = 'csv_to_notes';
/** Live recording surfaces. */
export const CAPTURE_METHOD_LIVE_SNAPSHOT = 'live_snapshot';
export const CAPTURE_METHOD_LIVE_USER_PHOTO = 'live_user_photo';
export const CAPTURE_METHOD_LIVE_RECORDING = 'live_recording';
/** The note a Live protocol run keeps its spoken transcript on (an ordinary note). */
export const CAPTURE_METHOD_LIVE_TRANSCRIPT = 'live_transcript';
/** Agent-authored, agent-copied, and agent-generated artifacts. */
export const CAPTURE_METHOD_AGENT_AUTHORED = 'agent_authored';
export const CAPTURE_METHOD_AGENT_COPY = 'agent_copy';
/**
 * A figure the data-analysis agent drew with matplotlib in the sandbox and the
 * pipe stored. Distinct from `image_upload` on purpose: these are machine
 * generated derivatives, and rolling them up as user uploads would misreport
 * both Capture Stream and the BigQuery cost attribution.
 */
export const CAPTURE_METHOD_AGENT_FIGURE = 'agent_figure';
/** Human-triggered duplicate (e.g. "Duplicate note" REST endpoint). Distinct
 * from agent_copy so analytics can split human vs AI-driven duplication. */
export const CAPTURE_METHOD_MANUAL_COPY = 'manual_copy';
/** Federated "Save to Bower" connector. */
export const CAPTURE_METHOD_CONNECTOR_SAVE = 'connector_save';
export const CAPTURE_METHODS = [
    CAPTURE_METHOD_MANUAL_TEXT,
    CAPTURE_METHOD_IMAGE_UPLOAD,
    CAPTURE_METHOD_AUDIO_UPLOAD,
    CAPTURE_METHOD_VIDEO_UPLOAD,
    CAPTURE_METHOD_PDF_UPLOAD,
    CAPTURE_METHOD_DOCX_UPLOAD,
    CAPTURE_METHOD_CSV_UPLOAD,
    CAPTURE_METHOD_XLSX_UPLOAD,
    CAPTURE_METHOD_EDITOR_PASTE,
    CAPTURE_METHOD_IMAGE_TO_TEXT,
    CAPTURE_METHOD_AUDIO_TO_TEXT,
    CAPTURE_METHOD_PDF_TO_TEXT,
    CAPTURE_METHOD_DOCX_TO_TEXT,
    CAPTURE_METHOD_CSV_TO_NOTES,
    CAPTURE_METHOD_LIVE_SNAPSHOT,
    CAPTURE_METHOD_LIVE_USER_PHOTO,
    CAPTURE_METHOD_LIVE_RECORDING,
    CAPTURE_METHOD_LIVE_TRANSCRIPT,
    CAPTURE_METHOD_AGENT_AUTHORED,
    CAPTURE_METHOD_AGENT_COPY,
    CAPTURE_METHOD_AGENT_FIGURE,
    CAPTURE_METHOD_MANUAL_COPY,
    CAPTURE_METHOD_CONNECTOR_SAVE,
];
const CAPTURE_METHOD_SET = new Set(CAPTURE_METHODS);
export function isValidCaptureMethod(value) {
    return CAPTURE_METHOD_SET.has(value);
}
// --- Legacy literals --------------------------------------------------------
//
// Existing rows in production carry these older values. Keep them recognised
// for read paths until backfill completes. NOT exported to encourage callers
// to use the canonical set above when writing new data.
export const LEGACY_CAPTURE_METHODS = ['manual', 'voice', 'text'];
// --- The one header not yet retired -----------------------------------------
/**
 * The legacy per-feature header, superseded by `BOWER_CLIENT_HEADER` in
 * `./client.ts`.
 *
 * Everything else about it went in Phase 6a: the client-declarable set, the
 * server's legacy mapping, and all five places that declared or verified it.
 * This constant survives for ONE reason — `packages/backend/src/app.ts` still
 * lists it in the CORS `allowedHeaders`, because a browser holding a frontend
 * build from before 2026-09-17 06:59 still sends it, and a preflight that
 * refuses a header the client sends rejects the WHOLE request, not one route.
 *
 * Nothing reads the header any more: such a request resolves to `api` instead
 * of `desktop`/`mobile`. That is a data-quality nick on a shrinking
 * population, which is the trade 6a deliberately makes.
 *
 * Delete this and the `app.ts` entry together, in Phase 6b, once those tabs
 * have turned over. The test is the preflight itself:
 * `curl -X OPTIONS https://api.bowerlabs.ai/api/artifacts -H 'Origin: ...'
 * -H 'Access-Control-Request-Headers: x-bower-capture-surface'` should stop
 * naming it in the response.
 */
export const CAPTURE_SURFACE_HEADER = 'x-bower-capture-surface';
// --- captureSurface ---------------------------------------------------------
export const CAPTURE_SURFACE_DESKTOP = 'desktop';
export const CAPTURE_SURFACE_MOBILE = 'mobile';
export const CAPTURE_SURFACE_LIVE_SESSION = 'live_session';
export const CAPTURE_SURFACE_AGENT_CHAT = 'agent_chat';
export const CAPTURE_SURFACE_CONNECTOR = 'connector';
export const CAPTURE_SURFACE_API = 'api';
/**
 * These two look like the odd members of this enum, provenance rather than a
 * surface, and a plan once proposed splitting them into their own column. That
 * was examined on 2026-09-17 and CLOSED. Both stay, for the same reason: both
 * are live query predicates.
 *
 * - `onboarding_import` is queried by `onboardingImportArtifactWhere`, and
 *   tested by `isOnboardingImportArtifact` on already-fetched rows. A metadata
 *   twin DOES exist (`metadata.upload.captureSurface`, off the resumable-upload
 *   sidecar) but covers THAT PATH ONLY: `POST /api/artifacts`,
 *   `POST /api/equipment`, derived notes and the multipart door all reach this
 *   column with no sidecar. No reader consults the twin regardless.
 * - `onboarding_seed` is queried by `onboarding-aha.service.ts` for the seeded
 *   protocol with no metadata fallback in that clause, and `search.service.ts`
 *   stamps its `seeded` flag straight off this value with no fallback either.
 *   Writers do also set `metadata.seeded`, and it is richer, but those readers
 *   do not use it.
 * - Beyond both members, two readers name no member at all and so survive any
 *   search for one. `note-creation.service.ts:784` gates an inherited surface
 *   through `isValidCaptureSurface`: delete a member and a derived note is
 *   written `api` instead, silently. The artifact list's `?device=` filter
 *   (`parseListArtifactsFilters`, `artifacts.list-filters.ts:91`) validates it
 *   against `z.enum([...CAPTURE_SURFACES])` and `artifact.query.service.ts`
 *   queries `captureSurface: { in: [...] }`; removing a member costs users
 *   nothing there, because no web UI sends `?device=` since the Capture Stream
 *   and its Device chip were retired (#4066), but it is still a live read of
 *   this column.
 *
 * So do not retire either member on the grounds that metadata already says it.
 * The test is not "provenance or surface", it is "is this value QUERIED".
 * Changing that answer means giving each query a metadata path first, which is
 * deliberate work, not a tidy-up.
 *
 * Reasoning in full: docs/superpowers/plans/2026-09-16-x-bower-client-migration.md
 */
/** System-seeded onboarding content (starter protocol, start-here note, ORCID imports). */
export const CAPTURE_SURFACE_ONBOARDING_SEED = 'onboarding_seed';
/** A file explicitly supplied by the acting user during the onboarding wizard. */
export const CAPTURE_SURFACE_ONBOARDING_IMPORT = 'onboarding_import';
export const CAPTURE_SURFACES = [
    CAPTURE_SURFACE_DESKTOP,
    CAPTURE_SURFACE_MOBILE,
    CAPTURE_SURFACE_LIVE_SESSION,
    CAPTURE_SURFACE_AGENT_CHAT,
    CAPTURE_SURFACE_CONNECTOR,
    CAPTURE_SURFACE_API,
    CAPTURE_SURFACE_ONBOARDING_SEED,
    CAPTURE_SURFACE_ONBOARDING_IMPORT,
];
const CAPTURE_SURFACE_SET = new Set(CAPTURE_SURFACES);
export function isValidCaptureSurface(value) {
    return CAPTURE_SURFACE_SET.has(value);
}
// --- MIME → captureMethod helpers ------------------------------------------
//
// Used by the OCR / transcription / extraction Cloud Task handlers to pick
// the right derived-note captureMethod from the source attachment's MIME.
/**
 * Map a source attachment's MIME type to the captureMethod the resulting
 * derived note should carry. Returns null when the MIME doesn't correspond
 * to a known extraction pipeline.
 */
export function captureMethodForExtractedNote(mimeType) {
    if (!mimeType)
        return null;
    const mime = mimeType.toLowerCase();
    if (mime.startsWith('image/'))
        return CAPTURE_METHOD_IMAGE_TO_TEXT;
    if (mime.startsWith('audio/'))
        return CAPTURE_METHOD_AUDIO_TO_TEXT;
    if (mime.startsWith('video/'))
        return CAPTURE_METHOD_AUDIO_TO_TEXT;
    if (mime === 'application/pdf')
        return CAPTURE_METHOD_PDF_TO_TEXT;
    if (mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        mime === 'application/msword') {
        return CAPTURE_METHOD_DOCX_TO_TEXT;
    }
    if (mime === 'text/csv' || mime === 'application/csv')
        return CAPTURE_METHOD_CSV_TO_NOTES;
    return null;
}
/**
 * Map a source attachment's MIME type to the captureMethod the raw upload
 * row itself should carry.
 */
export function captureMethodForUpload(mimeType) {
    if (!mimeType)
        return null;
    const mime = mimeType.toLowerCase();
    if (mime.startsWith('image/'))
        return CAPTURE_METHOD_IMAGE_UPLOAD;
    if (mime.startsWith('audio/'))
        return CAPTURE_METHOD_AUDIO_UPLOAD;
    if (mime.startsWith('video/'))
        return CAPTURE_METHOD_VIDEO_UPLOAD;
    if (mime === 'application/pdf')
        return CAPTURE_METHOD_PDF_UPLOAD;
    if (mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        mime === 'application/msword') {
        return CAPTURE_METHOD_DOCX_UPLOAD;
    }
    if (mime === 'text/csv' || mime === 'application/csv')
        return CAPTURE_METHOD_CSV_UPLOAD;
    return null;
}
/**
 * Surfaces a REQUEST BODY may never claim, whatever sends it.
 *
 * The WIRE axis is governed elsewhere and by a different vocabulary: a client
 * names ITSELF in `x-bower-client` (`BOWER_CLIENTS` in `./client.ts`) and the
 * server derives the surface from that. The header that let a client name a
 * surface directly, `x-bower-capture-surface`, is no longer READ (Phase 6 of
 * `docs/superpowers/plans/2026-09-16-x-bower-client-migration.md`). It is still
 * allowlisted for CORS until Phase 6b, so `CAPTURE_SURFACE_HEADER` below is
 * live, not vestigial.
 *
 * The body is a third axis and needs its own rule, because the agent service
 * is NOT a browser and legitimately posts `live_session` on two body doors —
 * and it authenticates with the acting user's own Firebase token, so the
 * backend cannot tell it apart from the web client and cannot grant it more
 * trust. Narrowing the body to what a browser may declare would therefore
 * silently drop a real surface: `tools/add_note.py` (a spoken note) posts
 * `live_session` with captureMethod `audio_to_text` to `POST /api/artifacts`,
 * which the server's `live_*` captureMethod inference does not rescue.
 *
 * So this is a deny-list of exactly the surfaces that (a) no legitimate client
 * ever sends in a body — verified by grep across the frontend and the agent
 * service — and (b) downstream logic TRUSTS, which is what makes a false claim
 * more than a mislabel:
 *
 *  - `onboarding_seed` flips content out of "the user's own work" for the
 *    personalization engine (`isSeededExample`) and stamps `seeded` on search
 *    results.
 *  - `connector` classifies provenance as a federated import
 *    (`personalized-prompt.service.ts` `originFor`, the `from_connector`
 *    analytics property).
 *
 * `live_session` and `agent_chat` are deliberately NOT here. A browser can
 * still claim either, and the honest cost is that a user can mislabel their
 * OWN rows inside their OWN workspace — a cosmetic error in the Capture Stream
 * filter, with no read-path or cross-tenant consequence. Closing that would
 * cost the live-agent paths above, which is the worse trade.
 */
export const SERVER_ASSIGNED_CAPTURE_SURFACES = [
    CAPTURE_SURFACE_ONBOARDING_SEED,
    CAPTURE_SURFACE_CONNECTOR,
];
const SERVER_ASSIGNED_SET = new Set(SERVER_ASSIGNED_CAPTURE_SURFACES);
export function isServerAssignedCaptureSurface(value) {
    return SERVER_ASSIGNED_SET.has(value);
}
/** The surfaces a request BODY may carry: everything except the deny-list. */
export const BODY_DECLARABLE_CAPTURE_SURFACES = CAPTURE_SURFACES.filter((surface) => !SERVER_ASSIGNED_SET.has(surface));
//# sourceMappingURL=capture.js.map